import { PrefetchPdfOnHover } from "@/components/shared/pdf-tools/components/prefetch-pdf-on-hover";
import { Check, ChevronDown, ChevronUp, Trash2 } from "lucide-react";
import { useRef } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useInvoiceBrowser } from "@/providers/invoice-browser";

type Props = {
  isDuplicate?: boolean;
  disableConfirm?: boolean;
  disableSkip?: boolean;
  disableBack?: boolean;
  onConfirmClick?: () => Promise<void>;
  onDelete?: () => Promise<void>;
  onRemoveDuplicates?: () => Promise<void>;
  confirmLabel?: string;
  showConfirmButton?: boolean;
  isEditing?: boolean;
};

export function NavigationBar(props: Props) {
  const {
    onDelete = () => {},
    onConfirmClick = () => Promise.resolve(),
    onRemoveDuplicates = () => {},
    disableConfirm = false,
    disableSkip = false,
    disableBack = false,
    isDuplicate = false,
    confirmLabel,
    showConfirmButton = true,
    isEditing = false,
  } = props;
  const { t } = useTranslation();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const nextButtonRef = useRef<HTMLButtonElement>(null);
  const leftButtonRef = useRef<HTMLButtonElement>(null);

  // Get the appropriate modifier key for the platform
  const modKey =
    typeof navigator !== "undefined" && navigator.platform.includes("Mac")
      ? "⌘"
      : "Ctrl";

  const {
    invoices: queue,
    next: browseNextInvoice,
    back: browseBackInvoice,
    remove: removeInvoiceFromQueue,
    canGoBack,
    canGoNext,
    currentIndex,
    getElementOnQueue,
  } = useInvoiceBrowser();

  const prevInvoiceId = canGoBack ? getElementOnQueue(currentIndex - 1) : null;
  const nextInvoiceId = canGoNext ? getElementOnQueue(currentIndex + 1) : null;

  const handleBackClick = async () => {
    if (queue.length === 0) {
      window.history.back();
    } else {
      browseBackInvoice();
    }
  };

  const handleSkipClick = async () => {
    browseNextInvoice();
  };

  const handleConfirmClick = async () => {
    if (isEditing) {
      await onConfirmClick();
      return;
    }

    setTimeout(() => {
      if (queue.length === 1) {
        window.history.back();
      } else {
        removeInvoiceFromQueue();
      }
    }, 100);

    onConfirmClick();
  };

  useHotkeys("mod+enter", () => buttonRef.current?.click());

  useHotkeys("mod+arrowup", () => {
    if (leftButtonRef.current) {
      leftButtonRef.current.classList.add("bg-gray-200");
      leftButtonRef.current.click();
      setTimeout(
        () => leftButtonRef.current?.classList.remove("bg-gray-200"),
        150,
      );
    }
  });

  useHotkeys("mod+arrowdown", () => {
    if (nextButtonRef.current) {
      nextButtonRef.current.classList.add("bg-gray-200");
      nextButtonRef.current.click();
      setTimeout(
        () => nextButtonRef.current?.classList.remove("bg-gray-200"),
        150,
      );
    }
  });

  return (
    <TooltipProvider>
      <div className="flex gap-2 w-full items-center">
        <Tooltip>
          <TooltipTrigger asChild>
            <PrefetchPdfOnHover invoiceId={prevInvoiceId}>
              <Button
                onClick={handleBackClick}
                tabIndex={-1}
                ref={leftButtonRef}
                variant="ghost"
                className="gap-1 hover:bg-gray-200"
                disabled={!canGoBack || queue.length === 0 || disableBack}
              >
                <ChevronUp />
                {t("back")}
              </Button>
            </PrefetchPdfOnHover>
          </TooltipTrigger>
          <TooltipContent>{modKey} ↑</TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <PrefetchPdfOnHover invoiceId={nextInvoiceId}>
              <Button
                tabIndex={-1}
                ref={nextButtonRef}
                onClick={handleSkipClick}
                variant="ghost"
                className="gap-1 hover:bg-gray-200"
                disabled={!canGoNext || disableSkip}
              >
                <ChevronDown />
                {t("next")}
              </Button>
            </PrefetchPdfOnHover>
          </TooltipTrigger>
          <TooltipContent>{modKey} ↓</TooltipContent>
        </Tooltip>
        <div className="flex-1" />
        <PrimaryAction
          modKey={modKey}
          isDuplicate={isDuplicate}
          onRemoveDuplicates={onRemoveDuplicates}
          onDelete={onDelete}
          disableBack={disableBack}
          showConfirmButton={showConfirmButton}
          confirmLabel={confirmLabel}
          buttonRef={buttonRef}
          handleConfirmClick={handleConfirmClick}
          disableConfirm={disableConfirm}
        />
      </div>
    </TooltipProvider>
  );
}

export type NavigationBarProps = Props;

const PrimaryAction = ({
  modKey,
  isDuplicate,
  onRemoveDuplicates,
  onDelete,
  disableBack,
  showConfirmButton,
  confirmLabel,
  buttonRef,
  handleConfirmClick,
  disableConfirm,
}: {
  modKey: string;
  isDuplicate: boolean;
  onRemoveDuplicates: () => void;
  onDelete: () => void;
  disableBack: boolean;
  showConfirmButton: boolean;
  confirmLabel?: string;
  buttonRef: React.RefObject<HTMLButtonElement>;
  handleConfirmClick: () => void;
  disableConfirm: boolean;
}) => {
  const { t } = useTranslation();

  if (isDuplicate) {
    return (
      <div className="flex gap-2">
        <Button onClick={onRemoveDuplicates} variant="outline">
          {t("not_a_duplicate")}
        </Button>
        <Button onClick={onDelete} disabled={disableBack}>
          <Trash2 className="mr-2 w-4 h-4" />
          {t("delete")}
        </Button>
      </div>
    );
  }

  if (showConfirmButton) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            ref={buttonRef}
            onClick={handleConfirmClick}
            disabled={disableConfirm}
          >
            <Check className="mr-2 w-4 h-4" />
            {confirmLabel ?? t("reviewed_and_next")}
          </Button>
        </TooltipTrigger>
        <TooltipContent>{modKey} ↵</TooltipContent>
      </Tooltip>
    );
  }

  return null;
};
